import React, { useState, useEffect, useCallback } from 'react';
import { vehicleRegistrationReportAPI, vehicleRegistrationAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import VehicleRegistrationReportTable from './VehicleRegistrationReportTable';
import VehicleRegistrationReportForm from './VehicleRegistrationReportForm';
import Swal from 'sweetalert2';
import './VehicleRegistrationReport.css';

const STATUS_FIELDS = [
  { key: 'inUseNotTransferred', label: 'Xe đang sử dụng chưa sang tên', shortLabel: 'Chưa sang tên' },
  { key: 'soldNotRevoked', label: 'Xe đã bán chưa thu hồi', shortLabel: 'Đã bán chưa thu hồi' },
  { key: 'transferRequested', label: 'Yêu cầu sang tên', shortLabel: 'Y/C sang tên' },
  { key: 'transferCompleted', label: 'Đã hoàn thành sang tên', shortLabel: 'Đã sang tên' },
  { key: 'broken', label: 'Xe hỏng', shortLabel: 'Xe hỏng' },
  { key: 'lost', label: 'Xe mất', shortLabel: 'Xe mất' },
  { key: 'systemUpdated', label: 'Cập nhật hệ thống', shortLabel: 'Cập nhật HT' },
  { key: 'transferredToInspection', label: 'Chuyển TTKS/XLVP/ĐTGQTN', shortLabel: 'Chuyển TTKS' },
  { key: 'receivedFromInspection', label: 'DS nhận từ TTKS/XLVP/ĐTGQTN', shortLabel: 'DS nhận TTKS' }
];

const VehicleRegistrationReportManagement = ({ user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [units, setUnits] = useState([]);

  // Filter & Search states
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchUnits = useCallback(async () => {
    try {
      const res = await vehicleRegistrationAPI.getUnits();
      const unitsData = res.data.units || res.data;
      setUnits(Array.isArray(unitsData) ? unitsData : []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        unitId: unitFilter,
        statusKey: statusFilter,
        reportDate: dateFilter
      };

      const res = await vehicleRegistrationReportAPI.getAll(params);
      if (res.data.success) {
        setData(res.data.data);
        setSummary(res.data.summary);
        setPagination(res.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching vehicle registration reports:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi',
        text: 'Có lỗi khi tải danh sách báo cáo ĐKX'
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, unitFilter, statusFilter, dateFilter]);

  useEffect(() => {
    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchUnits();
    }
  }, [user.role, fetchUnits]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData();
  };

  const handleOpenAddForm = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleOpenEditForm = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleSaveForm = async (formData) => {
    try {
      if (editingItem) {
        await vehicleRegistrationReportAPI.update(editingItem.id, formData);
        Swal.fire({
          icon: 'success',
          title: 'Thành công',
          text: 'Cập nhật rà soát sở hữu xe thành công!',
          timer: 1800,
          showConfirmButton: false
        });
      } else {
        await vehicleRegistrationReportAPI.create(formData);
        Swal.fire({
          icon: 'success',
          title: 'Thành công',
          text: 'Thêm mới rà soát sở hữu xe thành công!',
          timer: 1800,
          showConfirmButton: false
        });
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving form:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi',
        text: error.response?.data?.message || 'Có lỗi khi lưu dữ liệu'
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc chắn muốn xóa bản ghi này?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Đồng ý xóa',
      cancelButtonText: 'Hủy'
    });

    if (result.isConfirmed) {
      try {
        await vehicleRegistrationReportAPI.delete(id);
        Swal.fire({
          icon: 'success',
          title: 'Đã xóa',
          text: 'Xóa bản ghi thành công!',
          timer: 1800,
          showConfirmButton: false
        });
        fetchData();
      } catch (error) {
        console.error('Error deleting report:', error);
        Swal.fire({
          icon: 'error',
          title: 'Lỗi',
          text: error.response?.data?.message || 'Có lỗi khi xóa bản ghi'
        });
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const params = {
        search: searchTerm,
        unitId: unitFilter,
        statusKey: statusFilter,
        reportDate: dateFilter
      };

      const res = await vehicleRegistrationReportAPI.exportExcel(params);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ra_Soat_So_Huu_Xe_${new Date().getTime()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Thành công',
        text: 'Đã xuất file Excel rà soát sở hữu xe!',
        timer: 1800,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi',
        text: 'Có lỗi khi xuất file Excel'
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleStatusFilterClick = (statusKey) => {
    setStatusFilter(prev => (prev === statusKey ? '' : statusKey));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="vrr-management">
      {/* Header */}
      <div className="vrr-header">
        <h2 className="vrr-title">
          📑 Rà soát sở hữu xe
        </h2>
        {user?.unit && (
          <span className="vrr-unit-badge">
            Đơn vị: {user.unit.name}
          </span>
        )}
      </div>

      {/* Toolbar & Filter */}
      <div className="vrr-toolbar">
        <div className="vrr-filter-group">
          {/* Form tìm kiếm */}
          <form onSubmit={handleSearch} className="vrr-search-form">
            <input
              type="text"
              placeholder="Tìm biển số, ghi chú..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="vrr-search-input"
            />
            <button type="submit" className="vrr-btn vrr-btn-search">
              🔍 Tìm
            </button>
          </form>

          {/* Lọc đơn vị (Admin/Supervisor) */}
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <div className="vrr-select-unit">
              <SearchableSelect
                options={[
                  { value: '', label: 'Tất cả đơn vị' },
                  ...units.map(unit => ({ value: unit.id, label: unit.name }))
                ]}
                value={unitFilter}
                onChange={(val) => {
                  setUnitFilter(val);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Tất cả đơn vị"
              />
            </div>
          )}

          {/* Lọc theo ngày */}
          <input
            type="date"
            className="vrr-date-input"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            title="Lọc theo ngày rà soát"
          />

          {/* Lọc theo trạng thái */}
          <select
            className="vrr-select-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">📋 Tất cả trạng thái</option>
            {STATUS_FIELDS.map(field => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>

          {/* Nút xóa nhanh bộ lọc */}
          {(searchTerm || unitFilter || statusFilter || dateFilter) && (
            <button
              className="vrr-btn"
              style={{ background: '#e2e8f0', color: '#475569' }}
              onClick={() => {
                setSearchTerm('');
                setUnitFilter('');
                setStatusFilter('');
                setDateFilter('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              🔄 Xóa lọc
            </button>
          )}
        </div>

        {/* Nút chức năng */}
        <div className="vrr-action-group">
          <button className="vrr-btn vrr-btn-add" onClick={handleOpenAddForm}>
            ➕ Thêm mới
          </button>
          <button
            className="vrr-btn vrr-btn-export"
            onClick={handleExportExcel}
            disabled={exportingExcel}
          >
            {exportingExcel ? '⏳ Đang xuất...' : '📊 Xuất Excel'}
          </button>
        </div>
      </div>

      {/* Summary Box */}
      {summary && (
        <div className="vrr-summary-box">
          <div
            className={`vrr-summary-item vrr-summary-total ${!statusFilter ? 'active-filter' : ''}`}
            onClick={() => handleStatusFilterClick('')}
            title="Nhấn để xem tất cả"
          >
            <span className="vrr-summary-label">Tổng số xe rà soát</span>
            <span className="vrr-summary-value">{summary.total || 0}</span>
          </div>

          {STATUS_FIELDS.map(f => (
            <div
              key={f.key}
              className={`vrr-summary-item ${statusFilter === f.key ? 'active-filter' : ''}`}
              onClick={() => handleStatusFilterClick(f.key)}
              title={`Nhấn để lọc: ${f.label}`}
            >
              <span className="vrr-summary-label">{f.shortLabel}</span>
              <span className="vrr-summary-value" style={{ color: summary[f.key] > 0 ? '#1d4ed8' : '#64748b' }}>
                {summary[f.key] || 0}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Table Data */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '15px' }}>
          ⏳ Đang tải dữ liệu báo cáo...
        </div>
      ) : (
        <VehicleRegistrationReportTable
          data={data}
          page={pagination.page}
          limit={pagination.limit}
          user={user}
          onEdit={handleOpenEditForm}
          onDelete={handleDelete}
        />
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="vrr-pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Trước
          </button>
          <span className="vrr-page-info">
            Trang {pagination.page} / {pagination.totalPages} (Tổng số: {pagination.total} bản ghi)
          </span>
          <button
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Sau
          </button>
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <VehicleRegistrationReportForm
          initialData={editingItem}
          onSave={handleSaveForm}
          onCancel={() => setShowModal(false)}
          user={user}
          units={units}
        />
      )}
    </div>
  );
};

export default VehicleRegistrationReportManagement;
